/**
 * @file Appointment Token Detail Page — /cita-detalle/:token
 *
 * Public page (no auth). Confirm/cancel/reschedule via token.
 */
import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { CheckCircle2, PartyPopper, BellRing, AlertCircle } from "lucide-react";
import { publicService } from "../services/public";
import { useAuth } from "../hooks/useAuth";
import { authService } from "../services/auth";
import { useAppContext } from "../context/AppContext";
import AppointmentInfoCard from "../components/AppointmentInfoCard";
import { PROFILE_COLORS, DEFAULT_PROFILE_COLOR } from "../constants/profileColors";
import { FINAL_STATUSES } from "../../../shared/appointment-status.js";
import {
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogBody,
  DialogFooter,
  Spinner,
  Textarea,
} from "../ui";
import "./AppointmentTokenDetail.css";

const canConfirmAttendance = (dateTime) => {
  const now = new Date();
  const appt = new Date(dateTime);
  if (appt <= now) return false;
  const toCST = (d) => new Date(d.getTime() - 6 * 60 * 60 * 1000);
  const todayCST = toCST(now).toISOString().slice(0, 10);
  const apptCST = toCST(appt).toISOString().slice(0, 10);
  const tomorrow = new Date(toCST(now));
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowCST = tomorrow.toISOString().slice(0, 10);
  return apptCST === todayCST || apptCST === tomorrowCST;
};

export default function AppointmentTokenDetail() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const routeLocation = useLocation();
  const isNew = searchParams.get("nuevo") === "1";
  const isConfirmRoute = routeLocation.pathname.startsWith("/cita/confirmar/");
  const navigate = useNavigate();
  const { user, handleAuthSuccess } = useAuth();
  const { setPageColor } = useAppContext();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState("");

  const [email, setEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailStep, setEmailStep] = useState("email");
  const [emailSubmitting, setEmailSubmitting] = useState(false);

  useEffect(() => {
    if (isNew && user?.email && !user?.isEmailVerified && emailStep === "email") {
      setEmailStep("code");
    }
  }, [isNew, user?.email, user?.isEmailVerified]); // eslint-disable-line

  useEffect(() => {
    publicService
      .getAppointmentByToken(token)
      .then((res) => {
        setAppointment(res.data);
        if (isConfirmRoute && res.data.status === "AGENDADA") {
          if (canConfirmAttendance(res.data.dateTime)) {
            publicService
              .confirmAttendanceByToken(token)
              .then((confirmRes) => setAppointment(confirmRes.data))
              .catch(() => null);
          }
        }
      })
      .catch(() =>
        setError("No encontramos tu cita. El enlace puede ser inválido o haber expirado.")
      )
      .finally(() => setLoading(false));
  }, [token, isConfirmRoute]);

  const pc =
    PROFILE_COLORS[appointment?.professional?.profileColor] ||
    PROFILE_COLORS[DEFAULT_PROFILE_COLOR];

  useEffect(() => {
    setPageColor(pc.dark);
    return () => setPageColor(null);
  }, [pc.dark, setPageColor]);

  const themeStyle = useMemo(
    () => ({
      "--primary": pc.main,
      "--primary-deep": pc.dark,
      "--primary-50": pc[50],
    }),
    [pc]
  );

  const handleConfirmAttendance = async () => {
    setSubmitting(true);
    try {
      const res = await publicService.confirmAttendanceByToken(token);
      setAppointment(res.data);
    } catch (err) {
      setError(err.response?.data?.message || "No se pudo confirmar la asistencia");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    const trimmed = cancelReason.trim();
    if (!trimmed) {
      setCancelReasonError("El motivo de cancelación es obligatorio");
      return;
    }
    if (trimmed.length < 10) {
      setCancelReasonError("El motivo debe tener al menos 10 caracteres");
      return;
    }
    setSubmitting(true);
    try {
      const res = await publicService.cancelByToken(token, trimmed);
      setAppointment(res.data);
      setCancelOpen(false);
    } catch (err) {
      setError(err.response?.data?.message || "No se pudo cancelar la cita");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddEmail = async () => {
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) return;
    setEmailSubmitting(true);
    try {
      await authService.addEmail(email.trim());
      setEmailStep("code");
    } catch {
      // ignore
    } finally {
      setEmailSubmitting(false);
    }
  };

  const handleVerifyEmailCode = async () => {
    if (!/^\d{6}$/.test(emailCode)) return;
    setEmailSubmitting(true);
    try {
      const res = await authService.verifyEmailCode(emailCode);
      handleAuthSuccess(res.data);
      setEmailStep("done");
    } catch {
      // ignore
    } finally {
      setEmailSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="atd-loading">
        <Spinner size={36} />
        <p>Cargando tu cita...</p>
      </div>
    );
  }

  if (error && !appointment) {
    return (
      <div className="atd-page">
        <div className="auth-alert auth-alert-error">
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
        <Button variant="outline" onClick={() => navigate("/")}>
          Ir al inicio
        </Button>
      </div>
    );
  }

  if (!appointment) return null;

  const isFinal = FINAL_STATUSES.includes(appointment.status);
  const canConfirm = appointment.status === "AGENDADA" && canConfirmAttendance(appointment.dateTime);
  const proSlug = appointment.professional?.slug;

  return (
    <div className="atd-shell" style={themeStyle}>
      <div className="atd-page">
        {isNew && (
          <div className="atd-banner atd-banner-success">
            <PartyPopper size={16} />
            <span>¡Tu cita ha sido agendada con éxito! Te enviaremos un recordatorio el día anterior.</span>
          </div>
        )}

        {error && (
          <div className="auth-alert auth-alert-error">
            <AlertCircle size={14} />
            <span>{error}</span>
          </div>
        )}

        <Card>
          <AppointmentInfoCard
            appointment={appointment}
            title="Tu cita"
            showPatient
            linkPatient={false}
          />

          {appointment.status === "CONFIRMADA" && (
            <div className="atd-banner atd-banner-success" style={{ marginTop: 12 }}>
              <CheckCircle2 size={16} />
              <span>¡Asistencia confirmada! Te esperamos.</span>
            </div>
          )}

          {!isFinal && (
            <div className="atd-actions">
              {canConfirm && (
                <Button onClick={handleConfirmAttendance} loading={submitting}>
                  Confirmar asistencia
                </Button>
              )}
              {proSlug && (
                <Button variant="outline" onClick={() => navigate(`/c/${proSlug}?reagendar=${token}`)}>
                  Reagendar
                </Button>
              )}
              <Button variant="danger" onClick={() => setCancelOpen(true)} disabled={submitting}>
                Cancelar cita
              </Button>
            </div>
          )}
        </Card>

        {emailStep === "done" ? (
          <div className="atd-banner atd-banner-success">
            <CheckCircle2 size={16} />
            <span>¡Listo! Te enviaremos un recordatorio antes de tu cita.</span>
          </div>
        ) : isNew && user && !user.isEmailVerified ? (
          <Card>
            {emailStep === "code" || user?.email ? (
              <>
                <div className="atd-row">
                  <BellRing size={18} />
                  <strong>Verifica tu correo</strong>
                </div>
                <p className="atd-sub">
                  Enviamos un código de 6 dígitos a {user?.email || email.trim()}
                </p>
                <div className="atd-inline-form">
                  <input
                    className="auth-code-input"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={emailCode}
                    onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    onKeyDown={(e) => e.key === "Enter" && handleVerifyEmailCode()}
                  />
                  <Button
                    onClick={handleVerifyEmailCode}
                    loading={emailSubmitting}
                    disabled={emailCode.length !== 6}
                  >
                    Verificar
                  </Button>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => authService.addEmail(user?.email || email.trim())}
                  style={{ marginTop: 4, alignSelf: "flex-start" }}
                >
                  Reenviar código
                </Button>
              </>
            ) : (
              <>
                <div className="atd-row">
                  <BellRing size={18} />
                  <strong>Recibe un recordatorio antes de tu cita</strong>
                </div>
                <p className="atd-sub">Agrega tu correo y te avisamos el día anterior.</p>
                <div className="atd-inline-form">
                  <input
                    className="atd-email-input"
                    type="email"
                    placeholder="tu@correo.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddEmail()}
                  />
                  <Button
                    onClick={handleAddEmail}
                    loading={emailSubmitting}
                    disabled={!email.trim()}
                  >
                    Enviar código
                  </Button>
                </div>
              </>
            )}
          </Card>
        ) : !user ? (
          <Card>
            {isNew ? (
              <>
                <strong>Lleva el control de tus citas</strong>
                <p className="atd-sub">
                  Historial, recordatorios y más — todo en un solo lugar.
                </p>
                <div className="atd-actions">
                  <Button onClick={() => navigate("/register/patient")}>
                    Crear mi cuenta gratis
                  </Button>
                  <Button variant="ghost" onClick={() => navigate("/login")}>
                    Ya tengo cuenta
                  </Button>
                </div>
              </>
            ) : (
              <p className="atd-sub" style={{ margin: 0 }}>
                ¿Tienes más citas?{" "}
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="atd-link"
                >
                  Inicia sesión para verlas todas →
                </button>
              </p>
            )}
          </Card>
        ) : null}

        {appointment.status === "COMPLETADA" && proSlug && (
          <div style={{ textAlign: "center", marginTop: 12 }}>
            <Button onClick={() => navigate(`/c/${proSlug}`)}>Agendar otra cita</Button>
          </div>
        )}

        <Dialog open={cancelOpen} onOpenChange={(v) => { setCancelOpen(v); if (!v) setCancelReasonError(""); }}>
          <DialogContent size="sm" title="Cancelar cita">
            <DialogBody>
              <Textarea
                autoFocus
                rows={3}
                label="Motivo de cancelación"
                value={cancelReason}
                onChange={(e) => {
                  setCancelReason(e.target.value);
                  setCancelReasonError("");
                }}
                error={cancelReasonError}
              />
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setCancelOpen(false)}>Volver</Button>
              <Button variant="danger" onClick={handleCancel} loading={submitting}>
                Confirmar cancelación
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
